import { ChatRole, ChatResponseChoice } from '@quicktools/shared-components/services/chatApi/types';
import { nanoid } from 'nanoid';

import { REPHRASE_PROMPT_PART, TONE_PROMPT_PART } from '../../constants';
import chatApi from '../../services/chatApi';
import { BodyParam, PromptPartType, ResultType } from '../../utils/common/types';
import { CATEGORIES } from '../../constants/tool-categories';
import { LANGUAGES_WITHOUT_WORD_COUNT } from '../../constants/form-values';

import { manageChoices, removePrefixes } from './completionsRegex';
import { getUserSessionId } from './localStorage';
import { IValues, PromptPart } from './types';

const STR_TO_REPLACE = '{{value}}';

export function getParamsSystemPromptAndModelByCategory(selectedCategory: number) {
  const category = CATEGORIES[selectedCategory];

  return {
    params: category.params,
    model: category.model,
    system: category.systemRolePromptPart,
  };
}

function generatePromptPart({ type, template, variants }: PromptPart, input?: string) {
  switch (type) {
    case PromptPartType.template: {
      return template.replace(STR_TO_REPLACE, input);
    }
    case PromptPartType.stringVariants: {
      return variants[input];
    }
    case PromptPartType.string: {
      return template;
    }
    default: {
      throw new Error(`Type ${type} does not exist on prompt part`);
    }
  }
}

function generateRequestBody({
  values,
  selectedCategory,
}: {
  values: IValues;
  selectedCategory: number;
}) {
  const { additionalPromptPart, params } = CATEGORIES[selectedCategory];

  let body = '';

  params.forEach(({ value, noTrim, promptPart }) => {
    const userInput: string | boolean = values[value];

    const trimmedInput =
      typeof userInput === 'string' ? (noTrim ? userInput : userInput.trim()) : userInput;

    if (trimmedInput) {
      body += generatePromptPart(promptPart, trimmedInput as string);
    }
  });

  if (additionalPromptPart) {
    body += generatePromptPart(additionalPromptPart, body);
  }

  return body;
}

export function getParamsByCategory(values: IValues, params: BodyParam[]) {
  const obj = {};

  params.forEach((param) => {
    obj[`${param.analyticsValue}`] = values[`${param.value}`];
  });

  return obj;
}

export async function getCompletions({
  values,
  selectedCategory,
  generateMore,
}: {
  values: IValues;
  selectedCategory: number;
  generateMore: boolean;
}) {
  const { params, model, system } = getParamsSystemPromptAndModelByCategory(selectedCategory);

  const body = generateRequestBody({
    values,
    selectedCategory,
  });

  const messages = [
    {
      role: 'user' as ChatRole,
      content: body,
    },
  ];

  if (system) {
    messages.unshift({ role: 'system', content: system });
  }

  const bodyParams = getParamsByCategory(values, params);
  const { hasOneResult, splitChoices, generateMoreCount, generateCount } =
    CATEGORIES[selectedCategory];
  let n = hasOneResult || splitChoices ? 1 : 3;
  if (generateMore) {
    if (generateMoreCount) n = generateMoreCount;
  } else {
    if (generateCount) n = generateCount;
  }

  const result = await chatApi.getResponse({
    model,
    messages,
    user: getUserSessionId(),
    n: n,
  });

  const usage = result.payload.usage;
  const tokenInformation = {
    prompt_tokens: usage.prompt_tokens,
    completion_tokens: usage.completion_tokens,
  };

  const choicesToMap = manageChoices(selectedCategory, splitChoices, result.payload.choices);

  const actualResults = choicesToMap.map((choice) => ({
    id: nanoid(),
    type: ResultType.Common,
    isSensitive: choice.label === 'content_filter',
    result: removePrefixes(choice.text),
    showWordCount: !LANGUAGES_WITHOUT_WORD_COUNT.includes(values.language),
    rephrasedResults: [],
    isRephrasing: false,
  }));

  const textGenerated = choicesToMap.map((choice) => removePrefixes(choice.text));

  return { actualResults, textGenerated, bodyParams, tokenInformation };
}

const getRephraseBody = ({
  textToRephrase,
  tone,
  selectedCategory,
}: {
  textToRephrase: string;
  tone: string;
  selectedCategory: number;
}) => {
  const { rephrasePromptPart, params } = CATEGORIES[selectedCategory];

  let body = generatePromptPart(REPHRASE_PROMPT_PART, textToRephrase);
  if (params.find((p) => p.value === 'tone')) {
    body += generatePromptPart(TONE_PROMPT_PART, tone);
  }

  if (rephrasePromptPart) {
    body += generatePromptPart(rephrasePromptPart);
  }

  return body;
};

export async function getRephrasedTexts(
  selectedCategory: number,
  textToRephrase: string,
  tone: string,
  showWordCount: boolean,
) {
  const { model } = getParamsSystemPromptAndModelByCategory(selectedCategory);

  const body = getRephraseBody({
    textToRephrase,
    tone,
    selectedCategory,
  });
  const messages = [
    {
      role: 'user' as ChatRole,
      content: body,
    },
  ];

  const result = await chatApi.getResponse({
    model,
    messages,
    user: getUserSessionId(),
  });

  const choices = result.payload.choices;
  const usage = result.payload.usage;
  const tokenInformation = {
    prompt_tokens: usage.prompt_tokens,
    completion_tokens: usage.completion_tokens,
  };
  const rephrasedResults = choices.map((choice: ChatResponseChoice) => ({
    id: nanoid(),
    isSensitive: choice.finish_reason === 'content_filter',
    result: removePrefixes(choice.message.content),
    showWordCount,
    type: ResultType.Rephrase,
  }));

  return { rephrasedResults, tokenInformation };
}
